import { currentLocale, onLocaleChange, t } from "../i18n";
import { normaliseRegionGridColumns, resolveRegionMethods } from "../state";
import type { RegionEntry, RegionWarning, Store } from "../state";
import { resolveAccentRgb } from "../util/accent-rgb";
import { deleteRegions, mergeRegions } from "../util/region-ops";
import { pushUndoSnapshot } from "../util/region-undo";
import { computeRegionGeometries } from "../util/region-thumbnail";
import type { RegionGeometry } from "../util/region-thumbnail";
import {
  addRegionsToStack,
  createStack,
  disbandStack,
  nextUnusedStackN,
  removeRegionsFromStack,
} from "../util/stack-ops";
import { showRelinkDialog } from "./relink-dialog";
import { openRegionContextMenu, openStackContextMenu } from "./region-context-menu";
import {
  applyStackStyleChoice,
  chooseMergeSourceStyle,
  chooseStackStyle,
  cloneMethodSnapshot,
} from "./region-style-choice";
import { wrapRangeAsSlider } from "./custom-slider";
import { createInfoTooltip } from "./info-tooltip";

// ---------------------------------------------------------------------------
// region-list.ts - Item 7 + Item 16
//
// Renders a vertical, locale-sorted list of RegionEntry rows inside the
// section 01 (REGION MASK) sidebar body.
//
// Component contract:
//   createRegionList(parent, store) → root HTMLElement
//
// Behaviour:
//   • Sorted by userName (locale-aware, A9).
//   • Each row: 48×48 thumbnail | (name input + pixel-count) | ⚠ badge.
//   • Plain click → single-select (replaces selection with just this row).
//   • Cmd/Ctrl-click → toggle row in/out of selection. Falling back to the
//     clicked row alone when toggling would leave an empty selection.
//   • Shift-click → range-select from anchor to clicked row (inclusive, in
//     sorted display order). Anchor set only by plain clicks; unchanged by
//     shift/cmd clicks.
//   • Double-click name → inline rename. Blur / Enter commits.
//   • Empty state (no labelmap) → hint text instead of list.
//   • Geometries are cached; recomputed only when labelPlane ref or labels change.
//
// Multi-select keyboard (Item 16 §B):
//   • Arrow keys: move focus AND replace selection with the focused row.
//   • Shift+Arrow: extend selection (add focused row without dropping others).
//   • Cmd/Ctrl+Space: toggle focused row's membership.
//   • Home/End: move to first/last row (replaces selection, per existing behaviour).
//   • Enter/Space: confirm focused row (replaces selection).
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Geometry cache
// ---------------------------------------------------------------------------

const TOUCH_LONG_PRESS_SELECT_MS = 550;
const TOUCH_LONG_PRESS_MOVE_CANCEL_PX = 12;

type GeometryCache = {
  labelPlaneRef: Uint8Array;
  labelsKey: string; // JSON.stringify of sorted labels
  rgbKey: string; // serialised label→rgb map to detect tint changes
  geometries: RegionGeometry[];
};

function labelsKey(entries: RegionEntry[]): string {
  return JSON.stringify(entries.map((e) => e.label).sort((a, b) => a - b));
}

/** Stable string encoding of the label→rgb mapping so cache misses on tint changes. */
function rgbKey(entries: RegionEntry[]): string {
  return [...entries]
    .sort((a, b) => a.label - b.label)
    .map((e) => `${e.label}:${e.rgb[0]},${e.rgb[1]},${e.rgb[2]}`)
    .join(";");
}

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function rgbCss([r, g, b]: [number, number, number]): string {
  return `rgb(${r}, ${g}, ${b})`;
}

/** Draw an ImageData bitmap into a <canvas> element. */
function drawThumbnail(canvas: HTMLCanvasElement, thumbnail: ImageData): void {
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.putImageData(thumbnail, 0, 0);
}

/**
 * Render an `ImageData` preview using the live CSS accent tokens: mask-in
 * pixels get violet, mask-out pixels get yellow. The colours are resolved
 * from CSS variables so canvas accents stay in sync with the UI theme.
 *
 * R7-shipped F3 baseline.
 */
function makeAccentThumbnail(thumbnail: ImageData): ImageData {
  const violet = resolveAccentRgb("--violet", document.documentElement);
  const yellow = resolveAccentRgb("--yellow", document.documentElement);
  const out = new ImageData(thumbnail.width, thumbnail.height);
  for (let i = 0; i < thumbnail.data.length; i += 4) {
    const inMask = (thumbnail.data[i + 3] ?? 0) > 0;
    const rgb = inMask ? violet : yellow;
    out.data[i] = rgb[0];
    out.data[i + 1] = rgb[1];
    out.data[i + 2] = rgb[2];
    out.data[i + 3] = 255;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Create and mount the region list component.
 *
 * The root element is appended to `parent` and returned. The component
 * self-renders on every store change; geometry computation is cached by
 * (labelPlane reference, label set) so unchanged labelmaps do not retrigger
 * the two-phase pass.
 */
export function createRegionList(
  parent: HTMLElement,
  store: Store,
): HTMLElement {
  const root = document.createElement("div");
  // overflow-anchor: none prevents the browser from scroll-anchoring this
  // container when rows are inserted/removed (scroll-anchor fix, Phase 3).
  root.className = "region-list-root";
  parent.appendChild(root);

  // Geometry cache - initialised to a sentinel that never matches real state.
  let cache: GeometryCache | null = null;

  // ---------------------------------------------------------------------------
  // Item 16 - Multi-select state (§C)
  //
  // anchorUuid: the UUID of the last plain-clicked row. Used as the start of
  // a shift-click range selection. Reset when the labelmap changes (i.e. when
  // the regionLabelPlane reference changes). Plain clicks update it; shift/cmd
  // clicks leave it unchanged.
  //
  // lastLabelPlaneRef: tracks the most recently seen regionLabelPlane reference
  // so we can detect labelmap changes and reset the anchor.
  // ---------------------------------------------------------------------------

  let anchorUuid: string | null = null;
  let lastLabelPlaneRef: Uint8Array | null = null;
  let touchSelectMode = false;
  let suppressNextClickUuid: string | null = null;
  let suppressNextContextMenuUuid: string | null = null;
  const trackedModifiers = {
    meta: false,
    ctrl: false,
    shift: false,
  };

  const resetTrackedModifiers = (): void => {
    trackedModifiers.meta = false;
    trackedModifiers.ctrl = false;
    trackedModifiers.shift = false;
  };

  const handleModifierKey = (event: KeyboardEvent, isDown: boolean): void => {
    if (event.key === "Meta" || event.key === "OS" || event.key === "Command") {
      trackedModifiers.meta = isDown;
    }
    if (event.key === "Control") {
      trackedModifiers.ctrl = isDown;
    }
    if (event.key === "Shift") {
      trackedModifiers.shift = isDown;
    }
  };

  document.addEventListener("keydown", (event) => handleModifierKey(event, true), true);
  document.addEventListener("keyup", (event) => handleModifierKey(event, false), true);
  window.addEventListener("blur", resetTrackedModifiers);
  document.addEventListener("visibilitychange", resetTrackedModifiers);

  const toggleRegionSelection = (uuid: string): void => {
    const prev = store.get().activeRegionUuids;
    const inSelection = prev.includes(uuid);
    let next: string[];
    if (inSelection) {
      next = prev.filter((u) => u !== uuid);
      if (next.length === 0) next = [uuid];
    } else {
      next = [...prev, uuid];
    }
    store.patch({ activeRegionUuids: next });
  };

  const addRegionSelection = (uuid: string): void => {
    const prev = store.get().activeRegionUuids;
    const next = prev.includes(uuid) ? [...prev] : [...prev, uuid];
    store.patch({ activeRegionUuids: next.length > 0 ? next : [uuid] });
  };

  // ---------------------------------------------------------------------------
  // Toolbar builder - Selection + Stacks sections
  // ---------------------------------------------------------------------------

  function buildToolbarSection(
    ix: string,
    labelKey: string,
    variant: "yellow" | "violet",
    extraClass: string,
  ): { section: HTMLElement; body: HTMLDivElement } {
    const section = document.createElement("section");
    section.className = `section region-tools-section ${extraClass}`;

    const head = document.createElement("h3");
    head.className = `section-head ${variant}`;

    const label = document.createElement("span");
    label.textContent = t(labelKey);
    head.appendChild(label);

    const badge = document.createElement("span");
    badge.className = `ix ix-${variant}`;
    badge.textContent = ix;
    head.appendChild(badge);

    const body = document.createElement("div");
    body.className = "section-body region-tools-group-body";
    section.appendChild(head);
    section.appendChild(body);
    return { section, body };
  }

  function setDisabled(button: HTMLButtonElement, disabled: boolean): void {
    button.disabled = disabled;
    if (disabled) {
      button.setAttribute("aria-disabled", "true");
    } else {
      button.removeAttribute("aria-disabled");
    }
  }

  function buildToolbar(
    activeUuids: string[],
    hasStacks: boolean,
    anyActiveInStack: boolean,
  ): HTMLDivElement {
    const toolbar = document.createElement("div");
    toolbar.className = "region-list-toolbar region-tools-root";
    toolbar.setAttribute("role", "toolbar");
    toolbar.setAttribute("aria-label", t("aria.regionList.label"));

    const selection = buildToolbarSection(
      "02",
      "toolbar.group.selection",
      "yellow",
      "region-tools-selection",
    );
    const stacks = buildToolbarSection(
      "03",
      "toolbar.group.stacks",
      "violet",
      "region-tools-stacks",
    );

    // --- Select mode button ---
    const selectModeBtn = document.createElement("button");
    selectModeBtn.type = "button";
    selectModeBtn.className = "region-list-toolbar-btn";
    selectModeBtn.dataset["regionSelectModeToggle"] = "true";
    selectModeBtn.textContent = t("action.region.selectMode");
    selectModeBtn.setAttribute("aria-label", t("aria.regionToolbar.selectMode"));
    selectModeBtn.setAttribute("aria-pressed", String(touchSelectMode));
    selectModeBtn.classList.toggle("active", touchSelectMode);
    selectModeBtn.addEventListener("click", () => {
      touchSelectMode = !touchSelectMode;
      selectModeBtn.setAttribute("aria-pressed", String(touchSelectMode));
      selectModeBtn.classList.toggle("active", touchSelectMode);
    });
    selection.body.appendChild(selectModeBtn);

    // --- Merge button ---
    const mergeBtn = document.createElement("button");
    mergeBtn.type = "button";
    mergeBtn.className = "region-list-toolbar-btn";
    mergeBtn.textContent = t("action.region.merge");
    mergeBtn.setAttribute("aria-label", t("aria.regionToolbar.merge"));

    const canMerge = activeUuids.length >= 2;
    setDisabled(mergeBtn, !canMerge);

    mergeBtn.addEventListener("click", () => {
      const state = store.get();
      const selected = state.activeRegionUuids;
      if (selected.length < 2) return;
      const undoStack = pushUndoSnapshot(state);
      const targetUuid = selected[0]!;
      const sourceUuids = selected.slice(1);
      const sourceStyle = chooseMergeSourceStyle(state, targetUuid, sourceUuids);
      const imageSource = state.source ?? state.labelMap;
      const patch = mergeRegions(
        {
          regionEntries: state.regionEntries,
          regionLabelPlane: state.regionLabelPlane,
          activeRegionUuids: state.activeRegionUuids,
          width: imageSource?.width ?? 0,
          height: imageSource?.height ?? 0,
          labelMap: state.labelMap,
          regions: state.regions,
        },
        targetUuid,
        sourceUuids,
      );
      const regionEntries =
        sourceStyle === null
          ? patch.regionEntries
          : patch.regionEntries.map((entry) =>
              entry.uuid === targetUuid
                ? { ...entry, override: cloneMethodSnapshot(sourceStyle) }
                : entry,
            );
      store.patch({ ...patch, regionEntries, regionUndoStack: undoStack });
    });

    selection.body.appendChild(mergeBtn);

    // --- Delete button ---
    const deleteBtn = document.createElement("button");
    deleteBtn.type = "button";
    deleteBtn.className = "region-list-toolbar-btn";
    deleteBtn.textContent = t("action.region.delete");
    deleteBtn.setAttribute("aria-label", t("aria.regionToolbar.delete"));

    const canDelete = activeUuids.length >= 1;
    setDisabled(deleteBtn, !canDelete);

    deleteBtn.addEventListener("click", () => {
      const state = store.get();
      const selected = state.activeRegionUuids;
      if (selected.length < 1) return;
      const undoStack = pushUndoSnapshot(state);
      const delImageSource = state.source ?? state.labelMap;
      const patch = deleteRegions(
        {
          regionEntries: state.regionEntries,
          regionLabelPlane: state.regionLabelPlane,
          activeRegionUuids: state.activeRegionUuids,
          width: delImageSource?.width ?? 0,
          height: delImageSource?.height ?? 0,
          labelMap: state.labelMap,
          regions: state.regions,
        },
        selected,
      );
      store.patch({ ...patch, regionUndoStack: undoStack });
    });

    selection.body.appendChild(deleteBtn);
    toolbar.appendChild(selection.section);

    // --- Stack action group (Item 30) - Group 2 ---
    //
    // Four stack actions grouped together. All four use pushUndoSnapshot +
    // regionUndoStack to integrate with the existing undo machinery.

    // Create stack - enabled when at least one region is selected.
    const canCreateStack = activeUuids.length >= 1;
    const createStackBtn = document.createElement("button");
    createStackBtn.type = "button";
    createStackBtn.className = "region-list-toolbar-btn";
    createStackBtn.textContent = t("action.stack.create");
    createStackBtn.setAttribute("aria-label", t("aria.stack.create"));
    setDisabled(createStackBtn, !canCreateStack);

    createStackBtn.addEventListener("click", () => {
      const state = store.get();
      const selected = state.activeRegionUuids;
      if (selected.length < 1) return;
      const undoStack = pushUndoSnapshot(state);
      // Derive the localised prefix from label.stackN by substituting a
      // placeholder sequence number and stripping it back out. This makes
      // the prefix locale-aware without hardcoding it here.
      // e.g. "Stack {n}" with n="__N__" → "Stack __N__" → prefix "Stack"
      const PLACEHOLDER = "__N__";
      const stackNTemplate = t("label.stackN", { n: PLACEHOLDER });
      const prefix = stackNTemplate.replace(PLACEHOLDER, "").trim();
      // Find the lowest unused integer for the new stack's display name.
      const n = nextUnusedStackN(state.regionStacks, prefix);
      const userName = t("label.stackN", { n });
      const patch = createStack(state, { memberUuids: selected, userName });
      store.patch({ ...patch, regionUndoStack: undoStack });
    });

    stacks.body.appendChild(createStackBtn);

    // Add to stack - enabled when at least one region is selected AND at least
    // one stack exists. If exactly one stack exists, auto-add without a picker.
    // If multiple stacks exist, show a native <select> popover next to the button.
    const canAddToStack = activeUuids.length >= 1 && hasStacks;
    const addToStackBtn = document.createElement("button");
    addToStackBtn.type = "button";
    addToStackBtn.className = "region-list-toolbar-btn";
    addToStackBtn.textContent = t("action.stack.addTo");
    addToStackBtn.setAttribute("aria-label", t("aria.stack.addTo"));
    setDisabled(addToStackBtn, !canAddToStack);

    // Hidden <select> element used as a lightweight stack picker when multiple
    // stacks exist. Remains hidden until the Add-to-stack button is clicked;
    // hidden by default so it never takes toolbar space.
    const stackPicker = document.createElement("select");
    stackPicker.className = "region-list-stack-picker";
    stackPicker.setAttribute("aria-label", t("aria.stack.addTo"));
    // Keep the picker out of the default tab order; it is only shown on demand.
    stackPicker.tabIndex = -1;
    stackPicker.hidden = true;
    stacks.body.appendChild(stackPicker);

    addToStackBtn.addEventListener("click", () => {
      const state = store.get();
      const selected = state.activeRegionUuids;
      if (selected.length < 1 || state.regionStacks.length === 0) return;

      if (state.regionStacks.length === 1) {
        // Single stack: auto-add without a picker (no ambiguity).
        const undoStack = pushUndoSnapshot(state);
        const stackUuid = state.regionStacks[0]!.uuid;
        const styleChoice = chooseStackStyle(state, stackUuid, selected);
        const patch = addRegionsToStack(
          state,
          stackUuid,
          selected,
        );
        const regionEntries =
          patch.regionEntries === undefined
            ? undefined
            : applyStackStyleChoice(
                state,
                patch.regionEntries,
                selected,
                styleChoice,
              );
        const styledPatch =
          regionEntries === undefined ? patch : { ...patch, regionEntries };
        store.patch({ ...styledPatch, regionUndoStack: undoStack });
        return;
      }

      // Multiple stacks: populate and show the native <select> picker.
      // Start with a disabled placeholder so the change event fires for any real pick,
      // including the first option in the list (avoids the no-change-event bug).
      stackPicker.replaceChildren();
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = t("action.stack.addTo.placeholder");
      placeholder.disabled = true;
      placeholder.selected = true;
      stackPicker.appendChild(placeholder);

      for (const stack of state.regionStacks) {
        const opt = document.createElement("option");
        opt.value = stack.uuid;
        opt.textContent = stack.userName;
        stackPicker.appendChild(opt);
      }

      stackPicker.hidden = false;
      stackPicker.focus({ preventScroll: true });

      // One-time handler: when a stack is selected, apply the op.
      const onPickerChange = (): void => {
        const targetUuid = stackPicker.value;
        stackPicker.hidden = true;
        stackPicker.removeEventListener("change", onPickerChange);
        stackPicker.removeEventListener("blur", onPickerBlur);
        if (!targetUuid) return;
        const currentState = store.get();
        const undoStack = pushUndoSnapshot(currentState);
        const selectedUuids = currentState.activeRegionUuids;
        const styleChoice = chooseStackStyle(
          currentState,
          targetUuid,
          selectedUuids,
        );
        const patch = addRegionsToStack(
          currentState,
          targetUuid,
          selectedUuids,
        );
        const regionEntries =
          patch.regionEntries === undefined
            ? undefined
            : applyStackStyleChoice(
                currentState,
                patch.regionEntries,
                selectedUuids,
                styleChoice,
              );
        const styledPatch =
          regionEntries === undefined ? patch : { ...patch, regionEntries };
        store.patch({ ...styledPatch, regionUndoStack: undoStack });
      };

      // Dismiss picker without action on blur (e.g. pressing Escape then Tab).
      const onPickerBlur = (): void => {
        stackPicker.hidden = true;
        stackPicker.removeEventListener("change", onPickerChange);
        stackPicker.removeEventListener("blur", onPickerBlur);
      };

      stackPicker.addEventListener("change", onPickerChange);
      stackPicker.addEventListener("blur", onPickerBlur);
    });

    stacks.body.appendChild(addToStackBtn);

    // Remove from stack - enabled when at least one selected region is in a stack.
    const canRemoveFromStack = anyActiveInStack;
    const removeFromStackBtn = document.createElement("button");
    removeFromStackBtn.type = "button";
    removeFromStackBtn.className = "region-list-toolbar-btn";
    removeFromStackBtn.textContent = t("action.stack.removeFrom");
    removeFromStackBtn.setAttribute("aria-label", t("aria.stack.removeFrom"));
    setDisabled(removeFromStackBtn, !canRemoveFromStack);

    removeFromStackBtn.addEventListener("click", () => {
      const state = store.get();
      const selected = state.activeRegionUuids;
      if (selected.length < 1) return;
      const undoStack = pushUndoSnapshot(state);
      const patch = removeRegionsFromStack(state, selected);
      store.patch({ ...patch, regionUndoStack: undoStack });
    });

    stacks.body.appendChild(removeFromStackBtn);

    // Disband stack - enabled when at least one selected region belongs to a stack.
    // If the selected regions belong to multiple stacks, ALL of those stacks are
    // disbanded (simpler rule than "first only"; comment preserved here for clarity).
    const canDisbandStack = anyActiveInStack;
    const disbandStackBtn = document.createElement("button");
    disbandStackBtn.type = "button";
    disbandStackBtn.className = "region-list-toolbar-btn";
    disbandStackBtn.textContent = t("action.stack.disband");
    disbandStackBtn.setAttribute("aria-label", t("aria.stack.disband"));
    setDisabled(disbandStackBtn, !canDisbandStack);

    disbandStackBtn.addEventListener("click", () => {
      const state = store.get();
      const selected = state.activeRegionUuids;
      if (selected.length < 1) return;

      // Collect the set of stacks that contain any of the selected regions.
      const stackUuidsToDisband = new Set<string>();
      for (const entry of state.regionEntries) {
        if (entry.stackId !== null && selected.includes(entry.uuid)) {
          stackUuidsToDisband.add(entry.stackId);
        }
      }
      if (stackUuidsToDisband.size === 0) return;

      const undoStack = pushUndoSnapshot(state);

      // Disband all involved stacks sequentially, folding the patches together.
      let patchedState = state;
      const combinedPatch: {
        regionStacks?: typeof state.regionStacks;
        regionEntries?: typeof state.regionEntries;
      } = {};

      for (const uuid of stackUuidsToDisband) {
        const p = disbandStack(patchedState, uuid);
        if (p.regionStacks !== undefined)
          combinedPatch.regionStacks = p.regionStacks;
        if (p.regionEntries !== undefined)
          combinedPatch.regionEntries = p.regionEntries;
        // Fold the patch into a temporary state so the next disbandStack call
        // sees the already-updated stacks/entries.
        patchedState = { ...patchedState, ...p };
      }

      store.patch({ ...combinedPatch, regionUndoStack: undoStack });
    });

    stacks.body.appendChild(disbandStackBtn);
    toolbar.appendChild(stacks.section);

    return toolbar;
  }

  function buildThumbnailsSection(columns: number): {
    section: HTMLElement;
    body: HTMLDivElement;
  } {
    const section = document.createElement("section");
    section.className = "section region-thumbnails-section";

    const head = document.createElement("h3");
    head.className = "section-head yellow";

    const headLabelWrap = document.createElement("span");
    headLabelWrap.className = "section-head-label";

    const label = document.createElement("span");
    label.dataset["i18n"] = "section.regions";
    label.textContent = t("section.regions");
    headLabelWrap.appendChild(label);

    const tip = createInfoTooltip(t("tip.section.regions"));
    tip.element.classList.add("info-btn-wide");
    headLabelWrap.appendChild(tip.element);
    onLocaleChange(() => {
      label.textContent = t("section.regions");
      tip.setText(t("tip.section.regions"));
    });
    head.appendChild(headLabelWrap);

    const badge = document.createElement("span");
    badge.className = "ix ix-yellow";
    badge.textContent = "04";
    head.appendChild(badge);
    section.appendChild(head);

    const body = document.createElement("div");
    body.className = "section-body";

    const row = document.createElement("div");
    row.className = "row region-grid-columns-row";

    const labelWrap = document.createElement("span");
    labelWrap.className = "row-label";
    const rowLabel = document.createElement("label");
    rowLabel.htmlFor = "ctrl-regionGridColumns";
    rowLabel.textContent = t("param.regionGrid.columns");
    labelWrap.appendChild(rowLabel);

    const input = document.createElement("input");
    input.type = "range";
    input.id = "ctrl-regionGridColumns";
    input.min = "2";
    input.max = "5";
    input.step = "1";
    input.value = String(normaliseRegionGridColumns(columns));

    const value = document.createElement("span");
    value.className = "row-value";
    value.textContent = input.value;

    const stepper = document.createElement("span");
    stepper.className = "stepper-buttons";

    const step = (direction: -1 | 1): void => {
      input.value = String(
        normaliseRegionGridColumns(Number(input.value) + direction),
      );
      input.dispatchEvent(new Event("input", { bubbles: true }));
    };

    const up = document.createElement("button");
    up.type = "button";
    up.className = "step-button step-up";
    up.setAttribute("aria-label", "Increase value");
    up.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      step(1);
    });

    const down = document.createElement("button");
    down.type = "button";
    down.className = "step-button step-down";
    down.setAttribute("aria-label", "Decrease value");
    down.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      step(-1);
    });

    stepper.appendChild(up);
    stepper.appendChild(down);

    const cluster = document.createElement("span");
    cluster.className = "row-value-cluster";
    cluster.appendChild(wrapRangeAsSlider(input, "yellow"));
    cluster.appendChild(value);
    cluster.appendChild(stepper);

    input.addEventListener("input", () => {
      const next = normaliseRegionGridColumns(Number(input.value));
      input.value = String(next);
      value.textContent = String(next);
      store.patch({ uiRegionGridColumns: next });
    });

    row.appendChild(labelWrap);
    row.appendChild(cluster);
    body.appendChild(row);
    section.appendChild(body);
    return { section, body };
  }

  // ---------------------------------------------------------------------------
  // Stack header builder - Item 31
  // ---------------------------------------------------------------------------

  /**
   * Build a stack section header, optionally clickable for active-stack selection.
   *
   * U11: The big-thumbnail row path (M4) has been removed. Real stacks now render
   * as a thin section divider identical in structure to the Ungrouped pseudo-header,
   * with stack name and member count, no thumbnail. Rename is available via right-click
   * context menu (R7). The Ungrouped pseudo-header (stackUuid === null) is unchanged.
   *
   * @param stackName - Display name of the stack.
   * @param memberCount - Number of member entries in the stack.
   * @param stackUuid - UUID of the stack. When provided, the header is clickable and
   *   sets activeStackUuid on click. Pass null for the "Ungrouped" pseudo-header.
   * @param isActive - When true, renders with data-active="true" (Item 32 selected state).
   */
  function buildStackHeader(
    stackName: string,
    memberCount: number,
    stackUuid: string | null = null,
    isActive: boolean = false,
  ): HTMLDivElement {
    const header = document.createElement("div");
    header.className = "region-list-stack-header";
    header.setAttribute("role", "presentation");
    header.setAttribute("aria-hidden", "true");

    if (stackUuid !== null) {
      // Real stack: thin divider, clickable to select active stack.
      header.dataset["stackUuid"] = stackUuid;

      if (isActive) {
        header.dataset["active"] = "true";
        header.setAttribute("aria-current", "true");
      }

      header.addEventListener("click", () => {
        store.patch({ activeStackUuid: stackUuid, activeRegionUuids: [] });
      });
    }

    const nameSpan = document.createElement("span");
    nameSpan.className = "region-list-stack-header-name";
    nameSpan.textContent = stackName;
    header.appendChild(nameSpan);

    const countSpan = document.createElement("span");
    countSpan.className = "region-list-stack-header-count";
    countSpan.textContent = t("label.stackMemberCount", { n: memberCount });
    header.appendChild(countSpan);

    // Right-click context menu for real stacks only (UX-2).
    // The Ungrouped pseudo-header (stackUuid === null) has no context menu.
    if (stackUuid !== null) {
      // Inline rename: make the name span editable on demand.
      function startStackRename(): void {
        const original = nameSpan.textContent ?? stackName;
        // Swap the span for a temporary input field.
        const input = document.createElement("input");
        input.type = "text";
        input.className = "region-list-stack-header-name";
        input.value = original;
        input.setAttribute("aria-label", t("aria.regionList.rename"));
        header.replaceChild(input, nameSpan);
        input.focus({ preventScroll: true });
        input.select();

        function commitStackRename(): void {
          const trimmed = input.value.trim();
          const next = trimmed || original;
          // Restore the span with the new (or original) name.
          nameSpan.textContent = next;
          header.replaceChild(nameSpan, input);
          if (trimmed && trimmed !== original) {
            const state = store.get();
            const nextStacks = state.regionStacks.map((s) =>
              s.uuid === stackUuid ? { ...s, userName: next } : s,
            );
            store.patch({ regionStacks: nextStacks });
          }
        }

        input.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter") {
            ev.preventDefault();
            commitStackRename();
          }
          if (ev.key === "Escape") {
            input.value = original;
            commitStackRename();
          }
        });

        input.addEventListener("blur", commitStackRename, { once: true });

        // Prevent blur firing twice if Enter was pressed.
        input.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter") {
            input.removeEventListener("blur", commitStackRename);
          }
        });
      }

      // Double-click on header name span to rename inline.
      nameSpan.addEventListener("dblclick", (ev) => {
        ev.stopPropagation();
        startStackRename();
      });

      header.addEventListener("contextmenu", (ev) => {
        ev.preventDefault();
        // stopPropagation so the click handler above does not fire.
        ev.stopPropagation();
        openStackContextMenu({
          anchor: header,
          stackUuid,
          stackName: nameSpan.textContent ?? stackName,
          store,
          onRename: startStackRename,
        });
      });
    }

    return header;
  }

  // ---------------------------------------------------------------------------
  // Row builder
  // ---------------------------------------------------------------------------

  function buildRow(
    entry: RegionEntry,
    geometry: RegionGeometry | undefined,
    isActive: boolean,
    hasWarning: boolean,
    isFirstWhenNoneActive: boolean,
    warnings: RegionWarning[],
    displayUuids: string[],
    isFocused: boolean,
  ): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "region-list-row region-list-card";
    row.dataset["uuid"] = entry.uuid;
    // Tag with a queryable attribute so post-render re-queries can find the
    // freshly-mounted node by UUID (the old node is detached after replaceChildren).
    row.setAttribute("data-region-uuid", entry.uuid);
    if (isActive) row.classList.add("active");
    if (isFocused) row.classList.add("focused");
    if (hasWarning) row.classList.add("has-warning");
    // Listbox option semantics - roving tabindex pattern (Item 16 §D).
    // Exactly ONE row has tabIndex=0 at any time: the focused row. When no
    // row has keyboard focus, the first entry in display order gets tabIndex=0
    // so Tab lands somewhere sensible.
    row.setAttribute("role", "option");
    row.setAttribute("aria-selected", String(isActive));
    row.tabIndex = isFocused || isFirstWhenNoneActive ? 0 : -1;

    let longPressTimer: number | null = null;
    let longPressStartX = 0;
    let longPressStartY = 0;
    let longPressActivated = false;

    const clearLongPressTimer = (): void => {
      if (longPressTimer !== null) {
        window.clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    };

    const shouldStartLongPressSelect = (event: PointerEvent): boolean => {
      if (event.button !== 0) return false;
      if (event.pointerType !== "touch" && event.pointerType !== "pen") return false;
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return true;
      const blocked = target.closest(
        'input, button, select, textarea, [data-no-row-dblclick="true"], .region-list-badge',
      );
      return !(blocked && row.contains(blocked));
    };

    row.addEventListener("pointerdown", (event: PointerEvent) => {
      clearLongPressTimer();
      longPressActivated = false;
      if (!shouldStartLongPressSelect(event)) return;

      longPressStartX = event.clientX;
      longPressStartY = event.clientY;
      try {
        row.setPointerCapture(event.pointerId);
      } catch {
        // Pointer capture is best effort; selection still works without it.
      }

      longPressTimer = window.setTimeout(() => {
        longPressTimer = null;
        longPressActivated = true;
        touchSelectMode = true;
        suppressNextClickUuid = entry.uuid;
        suppressNextContextMenuUuid = entry.uuid;
        addRegionSelection(entry.uuid);
      }, TOUCH_LONG_PRESS_SELECT_MS);
    });

    row.addEventListener("pointermove", (event: PointerEvent) => {
      if (longPressTimer === null) return;
      const dx = event.clientX - longPressStartX;
      const dy = event.clientY - longPressStartY;
      if (
        dx * dx + dy * dy >
        TOUCH_LONG_PRESS_MOVE_CANCEL_PX * TOUCH_LONG_PRESS_MOVE_CANCEL_PX
      ) {
        clearLongPressTimer();
      }
    });

    row.addEventListener("pointerup", () => {
      clearLongPressTimer();
      if (longPressActivated) {
        longPressActivated = false;
      }
    });

    row.addEventListener("pointercancel", clearLongPressTimer);

    // --- Big thumbnail (48×48): mask shape rendered with region colour.
    // Replaces the small swatch as the primary visual identifier. ---
    const thumbWrap = document.createElement("div");
    thumbWrap.className = "region-list-thumbnail-wrap";
    thumbWrap.setAttribute("aria-hidden", "true");

    const thumbCanvas = document.createElement("canvas");
    thumbCanvas.className = "region-list-thumbnail";
    thumbCanvas.width = geometry?.thumbnail.width ?? 80;
    thumbCanvas.height = geometry?.thumbnail.height ?? 80;
    thumbCanvas.setAttribute("aria-hidden", "true");
    if (geometry?.thumbnail) {
      drawThumbnail(thumbCanvas, geometry.thumbnail);

      const hoverCanvas = document.createElement("canvas");
      hoverCanvas.className =
        "region-list-thumbnail region-list-thumbnail-hover";
      hoverCanvas.width = geometry.thumbnail.width;
      hoverCanvas.height = geometry.thumbnail.height;
      hoverCanvas.setAttribute("aria-hidden", "true");
      drawThumbnail(hoverCanvas, makeAccentThumbnail(geometry.thumbnail));
      thumbWrap.appendChild(hoverCanvas);
    } else {
      // Fallback: solid colour block when no geometry is available yet.
      const ctx = thumbCanvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = rgbCss(entry.rgb);
        ctx.fillRect(0, 0, thumbCanvas.width, thumbCanvas.height);
      }
    }
    thumbWrap.insertBefore(thumbCanvas, thumbWrap.firstChild);
    row.appendChild(thumbWrap);

    // --- Footer: name + edit icon + warning badge ---
    const rowMeta = document.createElement("div");
    rowMeta.className = "region-list-row-meta region-list-card-footer";

    // --- Name input ---
    const nameInput = document.createElement("input");
    nameInput.type = "text";
    nameInput.className = "region-list-name";
    nameInput.value = entry.userName;
    nameInput.readOnly = true;
    // In read-only state the row itself holds tab focus (roving tabindex).
    // The input becomes independently focusable only when editing is active.
    nameInput.tabIndex = -1;
    nameInput.setAttribute("aria-label", t("aria.regionList.rename"));

    function commitRename(): void {
      const trimmed = nameInput.value.trim();
      // Revert to the original name if blank.
      if (!trimmed) {
        nameInput.value = entry.userName;
        nameInput.readOnly = true;
        nameInput.tabIndex = -1;
        return;
      }
      if (trimmed === entry.userName) {
        nameInput.readOnly = true;
        nameInput.tabIndex = -1;
        return;
      }
      const state = store.get();
      const nextEntries = state.regionEntries.map((e) =>
        e.uuid === entry.uuid ? { ...e, userName: trimmed } : e,
      );
      store.patch({ regionEntries: nextEntries });
      nameInput.readOnly = true;
      nameInput.tabIndex = -1;
    }

    function startEdit(event?: Event): void {
      event?.stopPropagation();
      nameInput.readOnly = false;
      // Make the input independently focusable while editing.
      nameInput.tabIndex = 0;
      nameInput.focus({ preventScroll: true });
      nameInput.select();
    }

    nameInput.addEventListener("dblclick", (event) => {
      // Select the region first if it isn't the active one.
      const currentState = store.get();
      const isAlreadyActive = currentState.activeRegionUuids[0] === entry.uuid;
      if (!isAlreadyActive) {
        store.patch({ activeRegionUuids: [entry.uuid] });
        // After patch the row was re-rendered; the captured nameInput is now
        // detached. Re-query the persistent root for the fresh node and operate on it.
        const freshRow = root.querySelector<HTMLElement>(
          `[data-region-uuid="${CSS.escape(entry.uuid)}"]`,
        );
        const freshInput =
          freshRow?.querySelector<HTMLInputElement>(".region-list-name");
        if (freshInput) {
          freshInput.readOnly = false;
          freshInput.tabIndex = 0;
          freshInput.focus({ preventScroll: true });
          freshInput.select();
        }
      } else {
        startEdit(event); // already active, no re-render - use the closure
      }
    });

    nameInput.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        commitRename();
      }
      if (event.key === "Escape") {
        nameInput.value = entry.userName;
        nameInput.readOnly = true;
        nameInput.tabIndex = -1;
      }
    });

    nameInput.addEventListener("blur", () => {
      if (!nameInput.readOnly) {
        commitRename();
      }
    });

    // Prevent click on the input field from propagating to the row click
    // handler when the input is in read-only state (i.e. selecting the row).
    // In edit mode the click stays in the input so the cursor can be positioned.
    nameInput.addEventListener("click", (event) => {
      if (nameInput.readOnly) {
        // Propagate to the row click handler (selection).
        return;
      }
      event.stopPropagation();
    });

    rowMeta.appendChild(nameInput);

    // Pencil button removed (R9 C-1). Rename is via dblclick on the name input,
    // F2 keyboard shortcut, or right-click context menu.

    // Commit the meta column to the row after all children are added.
    row.appendChild(rowMeta);

    // --- Missing-asset badge (clickable relink trigger) ---
    if (hasWarning) {
      const badge = document.createElement("button");
      badge.type = "button";
      badge.className = "region-list-badge";
      badge.setAttribute("aria-label", t("aria.regionList.relink"));
      // Keep badge out of the listbox roving-tabindex so it doesn't disrupt
      // arrow-key navigation; it is accessible via click only.
      badge.tabIndex = -1;
      badge.textContent = "⚠";
      badge.addEventListener("click", (event) => {
        // Prevent the row-level click handler from also selecting the row.
        event.stopPropagation();
        // Find the first warning for this row: region-owned first, then
        // stack-owned (inherited from entry.stackId) as fallback.
        const warning =
          warnings.find(
            (w) => w.ownerType === "region" && w.ownerUuid === entry.uuid,
          ) ??
          (entry.stackId !== null
            ? warnings.find(
                (w) => w.ownerType === "stack" && w.ownerUuid === entry.stackId,
              )
            : undefined);
        if (!warning) return;
        showRelinkDialog({ parent: document.body, store, warning });
      });
      rowMeta.appendChild(badge);
    }

    // --- Row click → selection (Item 16 §A) ---
    // Plain click: replace selection; set anchor.
    // Cmd/Ctrl-click: toggle membership; leave anchor unchanged.
    // Shift-click: range-select from anchor to this row; leave anchor unchanged.
    row.addEventListener("click", (event: MouseEvent) => {
      if (suppressNextClickUuid === entry.uuid) {
        suppressNextClickUuid = null;
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      const isMeta =
        event.metaKey ||
        event.ctrlKey ||
        trackedModifiers.meta ||
        trackedModifiers.ctrl;
      const isShift = event.shiftKey || trackedModifiers.shift;

      if (isShift && !isMeta && anchorUuid !== null) {
        // Range selection: select all rows between anchor and clicked row
        // (inclusive) in the current visible display order (not alphabetical
        // sortedUuids - displayUuids mirrors the render order: stacks first,
        // then ungrouped).
        const anchorIdx = displayUuids.indexOf(anchorUuid);
        const clickedIdx = displayUuids.indexOf(entry.uuid);
        if (anchorIdx !== -1 && clickedIdx !== -1) {
          const lo = Math.min(anchorIdx, clickedIdx);
          const hi = Math.max(anchorIdx, clickedIdx);
          const rangeUuids = displayUuids.slice(lo, hi + 1);
          store.patch({ activeRegionUuids: rangeUuids });
        } else {
          // Fallback: anchor or clicked uuid not in display list; plain select.
          anchorUuid = entry.uuid;
          store.patch({ activeRegionUuids: [entry.uuid] });
        }
        // Anchor unchanged on shift-click.
      } else if (isMeta && !isShift) {
        // Toggle membership. If toggling out would leave selection empty,
        // keep the clicked row as the sole selection instead.
        toggleRegionSelection(entry.uuid);
        // Anchor unchanged on cmd-click.
      } else if (touchSelectMode) {
        toggleRegionSelection(entry.uuid);
      } else {
        // Plain click: replace selection and set new anchor.
        anchorUuid = entry.uuid;
        store.patch({ activeRegionUuids: [entry.uuid] });
      }
    });

    // --- Double-click on row → switch to Tone tab + select region (Phase 3) ---
    //
    // Guard: dblclick on any interactive child (name input, pencil, badge, swatch)
    // should be handled by that child, not by the row. Only a click on the row's
    // blank area (i.e. no interactive ancestor up to the row itself) triggers the
    // tab switch.
    row.addEventListener("dblclick", (e: MouseEvent) => {
      const target = e.target instanceof Element ? e.target : null;
      if (target) {
        const blocked = target.closest(
          'input, button, select, [data-no-row-dblclick="true"], .region-list-swatch, .region-list-name, .region-list-badge',
        );
        if (blocked && row.contains(blocked)) return;
      }
      e.preventDefault();
      const uuid = row.dataset["uuid"];
      if (!uuid) return;
      store.patch({
        uiActiveTab: "tone",
        activeRegionUuids: [uuid],
        activeStackUuid: null,
      });
    });

    // Right-click context menu (Item 9d).
    row.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      const clickedUuid = row.dataset["uuid"];
      if (!clickedUuid) return;
      if (suppressNextContextMenuUuid === clickedUuid) {
        suppressNextContextMenuUuid = null;
        e.stopPropagation();
        return;
      }
      openRegionContextMenu({
        x: e.clientX,
        y: e.clientY,
        store,
        anchor: row,
        regionUuid: clickedUuid,
        onRename: () => startEdit(),
      });
    });

    return row;
  }

  // ---------------------------------------------------------------------------
  // Virtual "Default" row - shown when labelMap === null
  // ---------------------------------------------------------------------------

  /**
   * Build a single read-only "Default" row to represent the virtual region
   * that is active when no labelmap is loaded.
   *
   * The row:
   *   - Uses the same `.region-list-row` class as real entries plus
   *     `.region-list-row--virtual` for muted styling.
   *   - Has aria-selected="true" and tabIndex={0} since it is the only option.
   *   - Has no colour swatch colour, no pixel-count, no thumbnail, no pencil
   *     button, and no missing-asset badge.
   *   - Click and keyboard events are no-ops: the listbox never changes state.
   */
  function buildVirtualDefaultRow(): HTMLDivElement {
    const row = document.createElement("div");
    row.className = "region-list-row region-list-row--virtual active";
    row.setAttribute("role", "option");
    row.setAttribute("aria-selected", "true");
    row.tabIndex = 0;

    // Neutral grey swatch placeholder (no real colour - no labelmap palette).
    const swatch = document.createElement("div");
    swatch.className = "region-list-swatch";
    swatch.style.backgroundColor = "var(--bg-sunk, #888)";
    swatch.setAttribute("aria-hidden", "true");
    row.appendChild(swatch);

    // Name cell - read-only, shows the i18n "Default" label.
    const nameEl = document.createElement("span");
    nameEl.className = "region-list-name";
    nameEl.textContent = t("label.regionList.default");
    row.appendChild(nameEl);

    // Clicking the virtual row is a no-op - no state change.
    row.addEventListener("click", (event) => {
      event.stopPropagation();
    });

    // Keyboard: the row should not move focus off itself since it is the only
    // option. Absorb arrow keys so the listbox handler (which looks for
    // [role="option"] siblings) stays inert. Also absorb Enter/Space so the
    // browser's default Space-scroll and any synthetic click are suppressed
    // (the virtual row is a no-op - no state change should occur).
    row.addEventListener("keydown", (event) => {
      switch (event.key) {
        case "ArrowDown":
        case "ArrowUp":
        case "Home":
        case "End":
        case "Enter":
        case " ":
          // Single option - nowhere to go; prevent default scroll/activation.
          event.preventDefault();
          break;
        default:
          break;
      }
    });

    return row;
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  function render(): void {
    const state = store.get();
    root.replaceChildren();

    const {
      regionEntries,
      regionLabelPlane,
      activeRegionUuids,
      activeStackUuid,
      regionStacks,
      source,
      labelMap,
      uiRegionGridColumns,
    } = state;

    // No-labelmap mode: show a single virtual "Default" entry.
    // Per Item 10 invariant: labelMap === null implies regionEntries is empty.
    // If somehow both are null/non-empty (corruption), prefer rendering real
    // entries (handled by falling through to the normal path below).
    if (labelMap === null) {
      const list = document.createElement("div");
      list.className = "region-list";
      list.setAttribute("role", "listbox");
      list.setAttribute("aria-label", t("aria.regionList.label"));
      list.setAttribute("aria-orientation", "vertical");
      list.setAttribute("aria-multiselectable", "true");
      list.appendChild(buildVirtualDefaultRow());
      root.appendChild(list);
      return;
    }

    // Edge case: labelmap loaded but no entries parsed yet (e.g. empty mask).
    if (regionEntries.length === 0) {
      const empty = document.createElement("p");
      empty.className = "region-list-empty";
      empty.textContent = t("label.regionList.empty");
      root.appendChild(empty);
      return;
    }

    // Resolve warnings - used for badge rendering and relink dialog targeting.
    const { warnings } = resolveRegionMethods(state);

    // Build a set of stack UUIDs that carry at least one warning.
    const warnedStackUuids = new Set<string>(
      warnings.filter((w) => w.ownerType === "stack").map((w) => w.ownerUuid),
    );

    // A row shows a warning badge when:
    //   a) the region itself owns a warning (ownerType === 'region', ownerUuid === entry.uuid), OR
    //   b) the region is a member of a stack that owns a warning (entry.stackId in warnedStackUuids).
    const warnedUuids = new Set<string>(
      regionEntries
        .filter(
          (e) =>
            warnings.some(
              (w) => w.ownerType === "region" && w.ownerUuid === e.uuid,
            ) ||
            (e.stackId !== null && warnedStackUuids.has(e.stackId)),
        )
        .map((e) => e.uuid),
    );

    // Compute (or reuse cached) region geometries.
    const currentLabelsKey = labelsKey(regionEntries);
    const currentRgbKey = rgbKey(regionEntries);
    let geometries: RegionGeometry[] = [];

    if (regionLabelPlane.length > 0) {
      const needsRecompute =
        cache === null ||
        cache.labelPlaneRef !== regionLabelPlane ||
        cache.labelsKey !== currentLabelsKey ||
        cache.rgbKey !== currentRgbKey;

      if (needsRecompute) {
        // Derive width/height from the available image dimension source.
        const imageSource = source ?? labelMap;
        const width = imageSource?.width ?? 0;
        const height = imageSource?.height ?? 0;

        if (width > 0 && height > 0) {
          const regionLabels = regionEntries.map((e) => e.label);
          const regionRgbByLabel = new Map<number, [number, number, number]>(
            regionEntries.map((e) => [e.label, e.rgb]),
          );

          geometries = computeRegionGeometries({
            labelPlane: regionLabelPlane,
            width,
            height,
            regionLabels,
            regionRgbByLabel,
            thumbnailSize: 80,
          });

          cache = {
            labelPlaneRef: regionLabelPlane,
            labelsKey: currentLabelsKey,
            rgbKey: currentRgbKey,
            geometries,
          };
        }
      } else {
        geometries = cache!.geometries;
      }
    }

    // Build a map of label → geometry for O(1) lookup when building rows.
    const geoByLabel = new Map<number, RegionGeometry>(
      geometries.map((g) => [g.label, g]),
    );

    // Sort entries by userName, locale-aware (A9).
    // The sort key uses the app's active locale and is re-computed on every
    // render - cheap because the list is bounded to ~64 entries.
    const locale = currentLocale();
    const sorted = [...regionEntries].sort((a, b) =>
      a.userName.localeCompare(b.userName, locale),
    );

    // Item 16 §C: reset anchor when the labelmap reference changes.
    if (lastLabelPlaneRef !== regionLabelPlane) {
      lastLabelPlaneRef = regionLabelPlane;
      // Set anchor to the first sorted uuid (or null when empty) as a sensible
      // starting point for shift-click range selection after a new labelmap load.
      anchorUuid = sorted[0]?.uuid ?? null;
    }

    // Sorted UUIDs in alphabetical order - kept for identity / diffing purposes
    // but NOT used for visible-order operations (shift-click range, roving tabindex).
    const sortedUuids = sorted.map((e) => e.uuid);

    // ---------------------------------------------------------------------------
    // Item 31 - Stack section headers (Approach A: single listbox).
    //
    // Build grouped entry list:
    //   1. One group per stack (in regionStacks creation order), each with a
    //      header row (role="presentation") and its member entries beneath it.
    //   2. A final "Ungrouped" section for entries with stackId === null.
    //
    // The Ungrouped header is shown only when at least one stack group was
    // actually rendered (otherwise all entries are ungrouped and a header adds
    // no clarity).
    //
    // Arrow-key navigation collects only [role="option"] elements, so headers
    // are automatically skipped. From the last entry of stack A, ↓ lands on
    // the first entry of stack B (not the header of B).
    // ---------------------------------------------------------------------------

    // Build a lookup: stackId → sorted entries for that stack.
    // Placed here (before displayUuids) so displayUuids can reflect visible order.
    const entryByStack = new Map<string, RegionEntry[]>();
    for (const stack of regionStacks) {
      entryByStack.set(stack.uuid, []);
    }
    const ungroupedEntries: RegionEntry[] = [];

    for (const entry of sorted) {
      if (entry.stackId !== null && entryByStack.has(entry.stackId)) {
        entryByStack.get(entry.stackId)!.push(entry);
      } else {
        ungroupedEntries.push(entry);
      }
    }

    // displayUuids: the exact render order (stack creation order → alphabetical
    // members → ungrouped alphabetical). Built BEFORE the render loop so that
    // shift-click range selection and the roving-tabindex fallback both use the
    // visible order rather than the raw alphabetical sortedUuids.
    //
    // Deliberate exception: sortedUuids is still used for the focusedActiveUuid
    // containment guard below - only membership (not position) is needed there.
    const displayUuids: string[] = [];
    for (const stack of regionStacks) {
      const members = entryByStack.get(stack.uuid) ?? [];
      for (const entry of members) {
        displayUuids.push(entry.uuid);
      }
    }
    for (const entry of ungroupedEntries) {
      displayUuids.push(entry.uuid);
    }

    // Determine which row currently has keyboard focus (by checking which uuid
    // is currently in the DOM's activeElement). This uuid drives the `.focused`
    // CSS class and the roving tabIndex=0 assignment.
    const focusedActiveUuid = (() => {
      const el = document.activeElement;
      if (!(el instanceof HTMLElement)) return null;
      const uuid = el.dataset["uuid"] ?? null;
      return uuid && sortedUuids.includes(uuid) ? uuid : null;
    })();

    const list = document.createElement("div");
    list.className = "region-list-grid";
    list.style.setProperty(
      "--region-grid-cols",
      String(normaliseRegionGridColumns(uiRegionGridColumns)),
    );
    // Listbox root - owns keyboard navigation for all option rows.
    list.setAttribute("role", "listbox");
    list.setAttribute("aria-label", t("aria.regionList.label"));
    list.setAttribute("aria-orientation", "vertical");
    // Item 16 §B: announce that multiple items may be selected.
    list.setAttribute("aria-multiselectable", "true");

    // Keyboard navigation on the listbox.
    //
    // Item 16 §B keyboard contract:
    //   Arrow keys:       move focus and replace selection with focused row.
    //   Shift+Arrow:      move focus and extend selection (add focused row).
    //   Cmd/Ctrl+Space:   toggle focused row membership.
    //   Home / End:       jump to first/last (replace selection).
    //   Enter / Space:    confirm focused row (replace selection).
    //   F2:               activate inline rename.
    list.addEventListener("keydown", (event) => {
      const rows = Array.from(
        list.querySelectorAll<HTMLElement>('[role="option"]'),
      );
      if (rows.length === 0) return;

      const focusedIndex = rows.findIndex((r) => document.activeElement === r);
      if (focusedIndex === -1) return;

      const isMeta = event.metaKey || event.ctrlKey;

      // Cmd/Ctrl+Space: toggle focused row membership.
      if ((event.key === " " || event.code === "Space") && isMeta) {
        event.preventDefault();
        const focusedRow = rows[focusedIndex];
        const uuid = focusedRow?.dataset["uuid"];
        if (uuid) {
          const cs = store.get();
          const prev = cs.activeRegionUuids;
          const inSel = prev.includes(uuid);
          let next: string[];
          if (inSel) {
            next = prev.filter((u) => u !== uuid);
            if (next.length === 0) next = [uuid];
          } else {
            next = [...prev, uuid];
          }
          store.patch({ activeRegionUuids: next });
          // Re-query to restore focus after re-render.
          const freshRow = root.querySelector<HTMLElement>(
            `[data-region-uuid="${CSS.escape(uuid)}"]`,
          );
          freshRow?.focus({ preventScroll: true });
        }
        return;
      }

      let nextIndex: number | null = null;

      switch (event.key) {
        case "ArrowDown":
          nextIndex = Math.min(focusedIndex + 1, rows.length - 1);
          break;
        case "ArrowUp":
          nextIndex = Math.max(focusedIndex - 1, 0);
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = rows.length - 1;
          break;
        case " ": {
          // Space: confirm/activate selection of the focused row.
          const focusedRow = rows[focusedIndex];
          const uuid = focusedRow?.dataset["uuid"];
          if (uuid) {
            event.preventDefault();
            anchorUuid = uuid;
            store.patch({ activeRegionUuids: [uuid] });
            const freshRow = root.querySelector<HTMLElement>(
              `[data-region-uuid="${CSS.escape(uuid)}"]`,
            );
            freshRow?.focus({ preventScroll: true });
          }
          return;
        }
        case "Enter":
        case "F2": {
          // H1: Enter activates inline rename (Mac convention, like Finder).
          // F2 also activates rename for cross-platform parity.
          const focusedRow = rows[focusedIndex];
          const uuid = focusedRow?.dataset["uuid"];
          if (uuid) {
            event.preventDefault();
            // Select the row if not already active; set anchor.
            const currentState = store.get();
            if (currentState.activeRegionUuids[0] !== uuid) {
              anchorUuid = uuid;
              store.patch({ activeRegionUuids: [uuid] });
            }
            const targetRow = root.querySelector<HTMLElement>(
              `[data-region-uuid="${CSS.escape(uuid)}"]`,
            );
            const nameEl =
              targetRow?.querySelector<HTMLInputElement>(".region-list-name");
            if (nameEl) {
              nameEl.readOnly = false;
              nameEl.tabIndex = 0;
              nameEl.focus({ preventScroll: true });
              nameEl.select();
            }
          }
          return;
        }
        default:
          return;
      }

      if (nextIndex !== null && nextIndex !== focusedIndex) {
        event.preventDefault();
        const nextRow = rows[nextIndex];
        if (nextRow) {
          const nextUuid = nextRow.dataset["uuid"];
          if (nextUuid) {
            if (event.shiftKey) {
              // Shift+Arrow: extend selection by adding the next row.
              const cs = store.get();
              const prev = cs.activeRegionUuids;
              const next = prev.includes(nextUuid) ? prev : [...prev, nextUuid];
              store.patch({ activeRegionUuids: next });
              // Anchor unchanged on Shift+Arrow.
            } else {
              // Plain arrow: replace selection and set anchor.
              anchorUuid = nextUuid;
              store.patch({ activeRegionUuids: [nextUuid] });
            }
            // Re-query for the freshly-rendered node via the persistent root:
            // store.patch() triggers a synchronous re-render that swaps out all
            // row elements via replaceChildren(), so nextRow and list are now
            // detached. Look up the new node by its data-region-uuid attribute.
            const freshRow = root.querySelector<HTMLElement>(
              `[data-region-uuid="${CSS.escape(nextUuid)}"]`,
            );
            freshRow?.focus({ preventScroll: true });
          }
        }
      }
    });

    const noneActive = activeRegionUuids.length === 0;
    // When nothing is focused and no row is active, the first entry in display
    // order gets tabIndex=0 to keep the list reachable by Tab.
    const firstEntryUuid = displayUuids[0];
    // When there is a selection but no keyboard focus, the first selected row
    // in display order becomes the roving-tabindex target so Tab returns to
    // a meaningful place within the list. Use displayUuids so the "first"
    // position reflects visual order (not alphabetical sortedUuids order).
    const firstSelectedUuid = displayUuids.find((u) =>
      activeRegionUuids.includes(u),
    );
    // The focused row: prefer the currently-focused DOM element's uuid; fall
    // back to the first selected entry (so Tab re-enters at a useful row); then
    // fall back to the first entry when the selection is empty.
    const rovingUuid =
      focusedActiveUuid ??
      firstSelectedUuid ??
      (noneActive ? firstEntryUuid : undefined);

    // Track whether at least one stack group was actually rendered (i.e. had
    // visible members). This is the correct gate for the Ungrouped header:
    // a stack that exists but has zero members should NOT cause the Ungrouped
    // header to appear (Minor 1 fix).
    let renderedAnyStack = false;

    // Helper to append a single entry row into the listbox.
    function appendEntryRow(entry: RegionEntry): void {
      const isActive = activeRegionUuids.includes(entry.uuid);
      // isFirstWhenNoneActive: keep as a fallback sentinel when rovingUuid
      // is undefined (empty list edge case) so at least one row has tabIndex=0.
      const isFirstWhenNoneActive =
        rovingUuid === undefined && entry.uuid === firstEntryUuid;
      const isFocused = entry.uuid === rovingUuid;
      const hasWarning = warnedUuids.has(entry.uuid);
      const geometry = geoByLabel.get(entry.label);
      list.appendChild(
        // Pass displayUuids (visible order) instead of sortedUuids so that
        // shift-click range selection operates on the rendered position.
        buildRow(
          entry,
          geometry,
          isActive,
          hasWarning,
          isFirstWhenNoneActive,
          warnings,
          displayUuids,
          isFocused,
        ),
      );
    }

    // Render stacked groups first (in creation order).
    for (const stack of regionStacks) {
      const members = entryByStack.get(stack.uuid) ?? [];
      if (members.length === 0) continue; // Skip stacks with no visible members.
      renderedAnyStack = true;
      // U11: thin divider header only -- no thumbnail row.
      list.appendChild(
        buildStackHeader(
          stack.userName,
          members.length,
          stack.uuid,
          activeStackUuid === stack.uuid,
        ),
      );
      for (const entry of members) {
        appendEntryRow(entry);
      }
    }

    // Render ungrouped entries at the bottom.
    // The "Ungrouped" header is shown only when at least one stacked group was
    // actually rendered - it would be redundant noise if every entry is ungrouped.
    if (ungroupedEntries.length > 0) {
      if (renderedAnyStack) {
        // Ungrouped pseudo-header is not clickable - pass null for stackUuid.
        list.appendChild(
          buildStackHeader(t("label.ungrouped"), ungroupedEntries.length),
        );
      }
      for (const entry of ungroupedEntries) {
        appendEntryRow(entry);
      }
    }

    // Item 15 (B): deduplicated missing-asset summary above the list rows.
    // Build a map of region uuid → userName for areaName resolution.
    const regionUuidToName = new Map<string, string>(
      regionEntries.map((e) => [e.uuid, e.userName]),
    );
    // Build a map of stack uuid → userName for stack-warning name resolution.
    const stackUuidToName = new Map<string, string>(
      regionStacks.map((s) => [s.uuid, s.userName]),
    );

    // Deduplicate by (ownerType, ownerUuid, assetKind) and collect unique lines.
    const seen = new Set<string>();
    const summaryLines: string[] = [];
    for (const w of warnings) {
      const key = `${w.ownerType}:${w.ownerUuid}:${w.assetKind}`;
      if (seen.has(key)) continue;
      seen.add(key);
      // Resolve the owner name: region entries use their own userName;
      // stack-owned warnings look up the stack by uuid.
      const areaName =
        w.ownerType === "stack"
          ? (stackUuidToName.get(w.ownerUuid) ?? t("label.stackUnknown"))
          : (regionUuidToName.get(w.ownerUuid) ??
            t("label.regionN", { n: "?" }));
      if (w.assetKind === "pattern") {
        summaryLines.push(t("status.asset.missing.pattern", { areaName }));
      } else {
        summaryLines.push(t("status.asset.missing.stampMask", { areaName }));
      }
    }

    // Item 30 - stack action enabled-state helpers.
    // hasStacks: at least one stack exists (required for "Add to stack").
    const hasStacks = regionStacks.length >= 1;
    // anyActiveInStack: at least one selected region has a non-null stackId.
    const activeSet = new Set<string>(activeRegionUuids);
    const anyActiveInStack = regionEntries.some(
      (e) => activeSet.has(e.uuid) && e.stackId !== null,
    );

    // Yuko R7 U1: Selection and Stacks intentionally render before the
    // thumbnails section so the toolbar can stay sticky above the grid.
    root.appendChild(
      buildToolbar(
        activeRegionUuids,
        hasStacks,
        anyActiveInStack,
      ),
    );

    const thumbnailsSection = buildThumbnailsSection(uiRegionGridColumns);

    if (summaryLines.length > 0) {
      const summary = document.createElement("p");
      summary.className = "region-list-warnings";
      summary.textContent = summaryLines.join(" ");
      thumbnailsSection.body.appendChild(summary);
    }

    thumbnailsSection.body.appendChild(list);
    root.appendChild(thumbnailsSection.section);
  }

  // ---------------------------------------------------------------------------
  // Subscribe + i18n
  // ---------------------------------------------------------------------------

  render();

  store.subscribe(() => {
    render();
  });

  // Re-render on locale change (badge / empty state labels, aria strings
  // are all resolved at render time via t(), so a full re-render is safe).
  onLocaleChange(() => {
    render();
  });

  return root;
}
